// SK-362: designate the people who administer a system portal.
import React, { useCallback, useEffect, useState } from 'react';
import { func, string } from 'prop-types';
import { connect } from 'react-redux';
import { Button, Confirm, Dropdown, Image, Message, Tab, Table } from 'semantic-ui-react';

import Section from '~/views/common/Section';
import { getUserID } from '~/state/ducks/auth/selectors';

import { addSystemAdmin, fetchSystemAdmins, removeSystemAdmin, searchUsers } from './operations';


export const SystemAdmins = ({ id, authorId, dispatch }) => {
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [options, setOptions] = useState([]);
    const [searching, setSearching] = useState(false);
    const [selected, setSelected] = useState(null);
    const [removing, setRemoving] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setAdmins(await dispatch(fetchSystemAdmins(id)));
        setLoading(false);
    }, [dispatch, id]);

    useEffect(() => { load(); }, [load]);

    const handleSearch = async (e, { searchQuery }) => {
        setSearching(true);
        const results = await dispatch(searchUsers(searchQuery));
        const current = new Set(admins.map(admin => admin.id));
        setOptions(results.filter(user => !current.has(user.id)).map(user => ({
            key: user.id,
            value: user.id,
            text: user.name,
            image: user.logo_url ? { avatar: true, src: user.logo_url } : undefined,
        })));
        setSearching(false);
    };

    const handleAdd = async () => {
        setSaving(true);
        if (await dispatch(addSystemAdmin(id, selected, authorId))) {
            setSelected(null);
            setOptions([]);
            await load();
        }
        setSaving(false);
    };

    const handleRemove = async () => {
        const userId = removing.id;
        setRemoving(null);
        setSaving(true);
        if (await dispatch(removeSystemAdmin(id, userId, authorId))) await load();
        setSaving(false);
    };

    return (
        <Tab.Pane loading={loading}>
            <Section name="System admins">
                <Message info size="small">
                    System admins administer this system portal. In Collaboratory they act
                    across all of the system&apos;s campuses, and their left-hand menu lists every campus.
                </Message>

                <Table compact data-testid="system-admins">
                    <Table.Body>
                        { admins.length === 0 && !loading &&
                            <Table.Row><Table.Cell>No system admins yet.</Table.Cell></Table.Row> }
                        { admins.map(admin => (
                            <Table.Row key={admin.id}>
                                <Table.Cell>
                                    { admin.logo_url && <Image avatar src={admin.logo_url} /> }
                                    { admin.name }
                                </Table.Cell>
                                <Table.Cell>{ admin.email }</Table.Cell>
                                <Table.Cell collapsing>
                                    <Button
                                        basic
                                        color="red"
                                        size="small"
                                        icon="user times"
                                        content="Remove"
                                        disabled={saving}
                                        onClick={() => setRemoving(admin)}
                                    />
                                </Table.Cell>
                            </Table.Row>
                        )) }
                    </Table.Body>
                </Table>

                <Dropdown
                    fluid
                    search
                    selection
                    clearable
                    placeholder="Add a system admin: search by name or email (3+ characters)"
                    options={options}
                    loading={searching}
                    value={selected}
                    onSearchChange={handleSearch}
                    onChange={(e, { value }) => setSelected(value || null)}
                    noResultsMessage="No matching users"
                />
                <Button
                    primary
                    style={{ marginTop: '0.75em' }}
                    icon="user plus"
                    content="Add system admin"
                    disabled={!selected || saving}
                    loading={saving}
                    onClick={handleAdd}
                />
            </Section>

            <Confirm
                open={removing !== null}
                header="Remove system admin"
                content={removing ? `${removing.name} stays in the portal as a member, but no longer administers the system.` : ''}
                confirmButton="Remove"
                onCancel={() => setRemoving(null)}
                onConfirm={handleRemove}
            />
        </Tab.Pane>
    );
};

SystemAdmins.propTypes = {
    id: string.isRequired,
    authorId: string,
    dispatch: func.isRequired,
};

export default connect(state => ({ authorId: getUserID(state) }))(SystemAdmins);
